
import { defineComponent, ref, onMounted, computed, watch} from "vue";
import { useRoute } from "vue-router";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import { Dropzone } from "dropzone";

import { hideModal } from "@/core/helpers/dom";
import ApiService from "@/core/services/ApiService";

interface Folder {
  id: string;
  folder: string;
}

export default defineComponent({
  name: "upload-application-file-modal",
  components: {},
  emit: ["submit"],
  props: {
    fileid: String,
  },
  setup(props, { emit }) {
    const route = useRoute();
    const uploadApplicationFileModalRef = ref<HTMLElement | null>(null);
    const submitButton = ref<HTMLButtonElement | null>(null);
    const folders = ref<Folder[]>([]);
    const selectedFolder = ref("");
    const newFolder = ref("");
    const dropRef = ref(null);
    const dropzone = ref<Dropzone>(null);
    const isAddedFile = ref(false);
    const folderType = ref(1);
    watch(
      () => props.fileid,
      async () => {
        if (props.fileid) {
          await formSubmit();
        }
      }
    );
    onMounted(async () => {
      if (dropRef.value !== null) {
        dropzone.value = new Dropzone(dropRef.value, {
          url: `${process.env.VUE_APP_API_URL}imgupload?category=ibo&fileid=${props.fileid}`,
          paramName: "upload", // The name that will be used to transfer the file
          maxFiles: 1,
          maxFilesize: 10, // MB
          addRemoveLinks: true,
          acceptedFiles: ".pdf,.jpg,.jpeg,",
          autoProcessQueue: false,
        });

        dropzone.value?.on("addedfile", function () {
          isAddedFile.value = true;
        });

        dropzone.value?.on("removedfile", function () {
          isAddedFile.value = false;
        });
      }

      setTimeout(async () => {
        const res = await ApiService.get(
          `merchants/files?merchant_id=${getMerchantId.value}&limit=100&group=folder`
        );
        if (res.data.success) {
          if (res.data.data !== null) {
            folders.value = [...res.data.data];
          }
        }
      }, 1000);
    });

    const getMerchantId = computed(() => {
      return route.params.id;
    });

    const formSubmit = async () => {
      if (!isAddedFile.value) {
        Swal.fire({
          text: "Please input the file!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "OK!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });

        return;
      }

      // eslint-disable-next-line
      submitButton.value!.disabled = true;
      // Activate indicator
      submitButton.value?.setAttribute("data-kt-indicator", "on");

      try {
        // eslint-disable-next-line
        const formData: any = new FormData();
        formData.append("upload", dropzone.value.getAcceptedFiles()[0]);

        const res = await ApiService.post(
          `imgupload?category=ibo&fileid=${props.fileid}`,
          formData
        );

        if (res.data.success) {
          Swal.fire({
            text: "Application File Successfully Uploaded!",
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: "Ok, got it!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          }).then(async function () {
            selectedFolder.value = "";
            newFolder.value = "";
            folderType.value = 1;
            const res = await ApiService.get(
              `merchants/files?merchant_id=${getMerchantId.value}&limit=100&group=folder`
            );
            if (res.data.success) {
              if (res.data.data !== null) {
                folders.value = [...res.data.data];
              }
            }

            emit("submit");
            hideModal(uploadApplicationFileModalRef.value);
          });
        } else {
          Swal.fire({
            text: res.data.error.message,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });
        }
      } catch (e) {
        Swal.fire({
          text: e.response.data.error
            ? e.response.data.error.message
            : "Something went wrong!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });
      }

      submitButton.value?.removeAttribute("data-kt-indicator");
      // eslint-disable-next-line
      submitButton.value!.disabled = false;
    };

    return {
      submitButton,
      folders,
      selectedFolder,
      newFolder,
      formSubmit,
      uploadApplicationFileModalRef,
      dropRef,
      folderType,
    };
  },
});
